import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Lock, Save, Loader2, Eye, EyeOff, Edit, MapPin, Phone, Mail, IdCard, Briefcase, User, Calendar, AlertTriangle
} from "lucide-react";
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { useAuth } from '@/contexts/AuthContext';
import { AuthService } from '@/services/authService';
import { toast } from 'sonner';

interface AdminProfile {
  nome_completo: string;
  email: string;
  telefone: string;
  cpf: string;
  data_nascimento: string;
  profissao: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  is_active: boolean;
  approved: boolean;
  aceitou_termos: boolean;
  data_aceitacao_termos: string;
  data_cadastro: string;
  contador_cancelamentos: number;
}

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [editedProfile, setEditedProfile] = useState<AdminProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [passwordData, setPasswordData] = useState({
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: ""
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setProfileLoading(true);
        setProfileError(null);

        const userData = await AuthService.getProfile();

        const profileData: AdminProfile = {
          nome_completo: userData.nome_completo,
          email: userData.email,
          telefone: userData.telefone,
          cpf: userData.cpf,
          data_nascimento: userData.data_nascimento ? new Date(userData.data_nascimento).toISOString().split('T')[0] : '',
          profissao: userData.profissao || '',
          cep: userData.cep || '',
          logradouro: userData.logradouro || '',
          numero: userData.numero || '',
          complemento: userData.complemento || '',
          bairro: userData.bairro || '',
          cidade: userData.cidade || '',
          estado: userData.estado || '',
          is_active: userData.is_active,
          approved: userData.approved,
          aceitou_termos: userData.aceitou_termos || false,
          data_aceitacao_termos: userData.data_aceitacao_termos || '',
          data_cadastro: userData.created_at,
          contador_cancelamentos: userData.contador_cancelamentos || 0
        };

        setProfile(profileData);
        setEditedProfile(profileData);
      } catch (error: unknown) {
        console.error('Erro ao carregar perfil:', error);
        setProfileError(error instanceof Error ? error.message : 'Erro ao carregar dados do perfil');
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    if (!editedProfile) return;

    try {
      setProfileSaving(true);
      setProfileError(null);

      const updateData = {
        nome_completo: editedProfile.nome_completo,
        telefone: editedProfile.telefone,
        data_nascimento: editedProfile.data_nascimento,
        profissao: editedProfile.profissao,
        cep: editedProfile.cep,
        logradouro: editedProfile.logradouro,
        numero: editedProfile.numero,
        complemento: editedProfile.complemento,
        bairro: editedProfile.bairro,
        cidade: editedProfile.cidade,
        estado: editedProfile.estado
      };

      await AuthService.updateProfile(updateData);

      const refreshedProfile = await AuthService.getProfile();
      const profileData: AdminProfile = {
        nome_completo: refreshedProfile.nome_completo,
        email: refreshedProfile.email,
        telefone: refreshedProfile.telefone,
        cpf: refreshedProfile.cpf,
        data_nascimento: refreshedProfile.data_nascimento ? new Date(refreshedProfile.data_nascimento).toISOString().split('T')[0] : '',
        profissao: refreshedProfile.profissao || '',
        cep: refreshedProfile.cep || '',
        logradouro: refreshedProfile.logradouro || '',
        numero: refreshedProfile.numero || '',
        complemento: refreshedProfile.complemento || '',
        bairro: refreshedProfile.bairro || '',
        cidade: refreshedProfile.cidade || '',
        estado: refreshedProfile.estado || '',
        is_active: refreshedProfile.is_active,
        approved: refreshedProfile.approved,
        aceitou_termos: refreshedProfile.aceitou_termos || false,
        data_aceitacao_termos: refreshedProfile.data_aceitacao_termos || '',
        data_cadastro: refreshedProfile.created_at,
        contador_cancelamentos: refreshedProfile.contador_cancelamentos || 0
      };

      setProfile(profileData);
      setEditedProfile(profileData);
      setIsEditing(false);
    } catch (error: unknown) {
      console.error('Erro ao salvar perfil:', error);
      setProfileError(error instanceof Error ? error.message : 'Erro ao salvar dados do perfil');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCancelProfile = () => {
    setEditedProfile(profile);
    setIsEditing(false);
    setProfileError(null);
  };

  const handleInputChange = (field: keyof AdminProfile, value: string) => {
    if (!editedProfile) return;

    setEditedProfile(prev => prev ? ({
      ...prev,
      [field]: value
    }) : null);
  };

  const handleChangePassword = async () => {
    try {
      // Validações básicas
      if (!passwordData.senhaAtual.trim()) {
        toast.error("Senha atual é obrigatória");
        return;
      }

      if (!passwordData.novaSenha.trim()) {
        toast.error("Nova senha é obrigatória");
        return;
      }

      if (passwordData.novaSenha.length < 6) {
        toast.error("Nova senha deve ter pelo menos 6 caracteres");
        return;
      }

      if (passwordData.novaSenha !== passwordData.confirmarSenha) {
        toast.error("As senhas não coincidem!");
        return;
      }

      if (passwordData.senhaAtual === passwordData.novaSenha) {
        toast.error("A nova senha deve ser diferente da senha atual");
        return;
      }
      
      setLoading(true);
      
      
      // Chamar API de alteração de senha
      const response = await AuthService.changePassword({
        currentPassword: passwordData.senhaAtual,
        newPassword: passwordData.novaSenha,
        confirmPassword: passwordData.confirmarSenha
      });
      
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
        return;
      }
      
      // Limpar campos
      setPasswordData({
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: ""
      });
      
      
    } catch (error: unknown) {
      console.error('Erro ao alterar senha:', error);
      toast.error(error instanceof Error ? error.message : "Erro ao alterar senha!");
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <ResponsiveAdminLayout
        title="Perfil"
        subtitle="Carregando seus dados..."
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p>Carregando perfil...</p>
          </div>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  if (profileError || !profile || !editedProfile) {
    return (
      <ResponsiveAdminLayout
        title="Perfil"
        subtitle="Erro ao carregar dados"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Erro ao carregar perfil</h3>
            <p className="text-gray-600 mb-4">{profileError || 'Dados não encontrados'}</p>
            <Button onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveAdminLayout
      title="Perfil"
      subtitle="Gerencie suas informações pessoais e senha de acesso"
    >
      <div className="w-full space-y-6">
        <div className="mb-6 flex items-center justify-end">
          {profileError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <p className="text-red-700">{profileError}</p>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCancelProfile}
                  disabled={profileSaving}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                >
                  {profileSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {profileSaving ? 'Salvando...' : 'Salvar'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)} className="bg-blue-600 hover:bg-blue-700">
                <Edit className="w-4 h-4 mr-2" />
                Editar Perfil
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações Pessoais</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="personal" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="personal">Dados Pessoais</TabsTrigger>
                <TabsTrigger value="address">Endereço</TabsTrigger>
              </TabsList>
              
              <TabsContent value="personal" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nome_completo" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Nome Completo
                    </Label>
                    <Input
                      id="nome_completo"
                      value={isEditing ? editedProfile.nome_completo : profile.nome_completo}
                      onChange={(e) => handleInputChange('nome_completo', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={isEditing ? editedProfile.email : profile.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="telefone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Telefone
                    </Label>
                    <Input
                      id="telefone"
                      value={isEditing ? editedProfile.telefone : profile.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      disabled={!isEditing}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cpf" className="flex items-center gap-2">
                      <IdCard className="w-4 h-4" />
                      CPF
                    </Label>
                    <Input
                      id="cpf"
                      value={isEditing ? editedProfile.cpf : profile.cpf}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      disabled={true}
                      placeholder="123.456.789-01"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="data_nascimento" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Data de Nascimento
                    </Label>
                    <Input
                      id="data_nascimento"
                      type="date"
                      value={isEditing ? editedProfile.data_nascimento : profile.data_nascimento}
                      onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="profissao" className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Profissão
                    </Label>
                    <Input
                      id="profissao"
                      value={isEditing ? editedProfile.profissao : profile.profissao}
                      onChange={(e) => handleInputChange('profissao', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Sua profissão"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
                  <div>
                    <Label>Data de Cadastro</Label>
                    <p>{new Date(profile.data_cadastro).toLocaleDateString('pt-BR')}</p>
                  </div>
                  {profile.data_aceitacao_termos && (
                    <div>
                      <Label>Termos Aceitos em</Label>
                      <p>{new Date(profile.data_aceitacao_termos).toLocaleDateString('pt-BR')}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="address" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="cep" className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      CEP
                    </Label>
                    <Input
                      id="cep"
                      value={isEditing ? editedProfile.cep : profile.cep}
                      onChange={(e) => handleInputChange('cep', e.target.value)}
                      disabled={!isEditing}
                      placeholder="12345-678"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado</Label>
                    <Input
                      id="estado"
                      value={isEditing ? editedProfile.estado : profile.estado}
                      onChange={(e) => handleInputChange('estado', e.target.value)}
                      disabled={!isEditing}
                      placeholder="SP"
                      maxLength={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={isEditing ? editedProfile.cidade : profile.cidade}
                      onChange={(e) => handleInputChange('cidade', e.target.value)}
                      disabled={!isEditing}
                      placeholder="São Paulo"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="logradouro">Logradouro</Label>
                  <Input
                    id="logradouro"
                    value={isEditing ? editedProfile.logradouro : profile.logradouro}
                    onChange={(e) => handleInputChange('logradouro', e.target.value)}
                    disabled={!isEditing}
                    placeholder="Rua das Flores"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={isEditing ? editedProfile.bairro : profile.bairro}
                    onChange={(e) => handleInputChange('bairro', e.target.value)}
                    disabled={!isEditing}
                    placeholder="Centro"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={isEditing ? editedProfile.numero : profile.numero}
                      onChange={(e) => handleInputChange('numero', e.target.value)}
                      disabled={!isEditing}
                      placeholder="123"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={isEditing ? editedProfile.complemento : profile.complemento}
                      onChange={(e) => handleInputChange('complemento', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Apto 45"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Alterar Senha
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Para sua segurança, use uma senha forte com pelo menos 6 caracteres, 
              incluindo letras maiúsculas, minúsculas e números.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Informações do usuário */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Usuário Atual</h3>
                <p className="text-sm text-blue-700">
                  <strong>Nome:</strong> {user?.nome_completo || 'Administrador'}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Email:</strong> {user?.email || 'admin@vitasana.com'}
                </p>
              </div>

              {/* Formulário de alteração de senha */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="senha-atual-input">Senha Atual</Label>
                  <div className="relative">
                    <Input
                      id="senha-atual-input"
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.senhaAtual}
                      onChange={(e) => setPasswordData({...passwordData, senhaAtual: e.target.value})}
                      placeholder="Digite sua senha atual"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="nova-senha-input">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="nova-senha-input"
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.novaSenha}
                      onChange={(e) => setPasswordData({...passwordData, novaSenha: e.target.value})}
                      placeholder="Digite sua nova senha"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Mínimo 6 caracteres, incluindo letras e números
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmar-senha-input">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmar-senha-input"
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirmarSenha}
                      onChange={(e) => setPasswordData({...passwordData, confirmarSenha: e.target.value})}
                      placeholder="Confirme sua nova senha"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Indicador de força da senha */}
                {passwordData.novaSenha && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700">Força da senha:</p>
                    <div className="space-y-1">
                      <div className={`text-xs ${passwordData.novaSenha.length >= 6 ? 'text-green-600' : 'text-red-600'}`}>
                        ✓ Pelo menos 6 caracteres {passwordData.novaSenha.length >= 6 ? '✅' : '❌'}
                      </div>
                      <div className={`text-xs ${/[A-Z]/.test(passwordData.novaSenha) ? 'text-green-600' : 'text-red-600'}`}>
                        ✓ Pelo menos uma letra maiúscula {/[A-Z]/.test(passwordData.novaSenha) ? '✅' : '❌'}
                      </div>
                      <div className={`text-xs ${/[a-z]/.test(passwordData.novaSenha) ? 'text-green-600' : 'text-red-600'}`}>
                        ✓ Pelo menos uma letra minúscula {/[a-z]/.test(passwordData.novaSenha) ? '✅' : '❌'}
                      </div>
                      <div className={`text-xs ${/\d/.test(passwordData.novaSenha) ? 'text-green-600' : 'text-red-600'}`}>
                        ✓ Pelo menos um número {/\d/.test(passwordData.novaSenha) ? '✅' : '❌'}
                      </div>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={handleChangePassword} 
                  disabled={loading || !passwordData.senhaAtual || !passwordData.novaSenha || !passwordData.confirmarSenha}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  {loading ? 'Alterando Senha...' : 'Alterar Senha'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Dicas de Segurança</h3>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Use uma senha única que você não usa em outros lugares</li>
                  <li>• Combine letras maiúsculas, minúsculas, números e símbolos</li>
                  <li>• Evite informações pessoais como nome, data de nascimento</li>
                  <li>• Altere sua senha regularmente</li>
                  <li>• Nunca compartilhe sua senha com outras pessoas</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ResponsiveAdminLayout>
  );
};

export default AdminSettings; 
